/**
 * Parses assistant message for embedded finance-match block (from find_potential_matches).
 * Agent emits <finance-match>JSON</finance-match> XML; this extracts it for MatchSuggestionCard.
 */

export interface FinanceMatchSignal {
  label: string;
  score: number;
}

export interface FinanceMatchCandidate {
  case_id: string;
  confidence: number;
  confidence_summary: string;
  signals?: FinanceMatchSignal[];
}

export interface FinanceMatchPayload {
  bank_line_id: string;
  candidates: FinanceMatchCandidate[];
}

export interface ExtractFinanceMatchResult {
  matchPayload: FinanceMatchPayload | null;
  contentWithoutBlock: string;
}

/** Matches <finance-match>...</finance-match> (agent emits this XML). */
const FINANCE_MATCH_TAG_RE = /<finance-match>\s*([\s\S]*?)\s*<\/finance-match>/;

function isValidPayload(obj: unknown): obj is FinanceMatchPayload {
  if (obj == null || typeof obj !== 'object') return false;
  const o = obj as Record<string, unknown>;
  if (typeof o.bank_line_id !== 'string' || !Array.isArray(o.candidates)) return false;
  if (o.candidates.length === 0) return false;
  for (const c of o.candidates) {
    if (
      c == null ||
      typeof c !== 'object' ||
      typeof (c as Record<string, unknown>).case_id !== 'string' ||
      typeof (c as Record<string, unknown>).confidence !== 'number' ||
      typeof (c as Record<string, unknown>).confidence_summary !== 'string'
    )
      return false;
    const sig = (c as Record<string, unknown>).signals;
    if (sig !== undefined) {
      if (!Array.isArray(sig)) return false;
      for (const item of sig) {
        if (
          item == null ||
          typeof item !== 'object' ||
          typeof (item as Record<string, unknown>).label !== 'string' ||
          typeof (item as Record<string, unknown>).score !== 'number'
        )
          return false;
      }
    }
  }
  return true;
}

/**
 * Extracts <finance-match>JSON</finance-match> from assistant message content.
 * Returns the parsed payload (if valid) and the message with that tag removed.
 */
export function extractFinanceMatchBlock(content: string): ExtractFinanceMatchResult {
  const match = content.match(FINANCE_MATCH_TAG_RE);
  if (!match) {
    return { matchPayload: null, contentWithoutBlock: content };
  }

  const fullMatch = match[0];
  const jsonRaw = match[1]?.trim() ?? '';
  const contentWithoutBlock = content
    .replace(fullMatch, '')
    .trim()
    .replace(/\n{3,}/g, '\n\n');

  try {
    const parsed = JSON.parse(jsonRaw) as unknown;
    if (!isValidPayload(parsed)) {
      return { matchPayload: null, contentWithoutBlock };
    }
    return { matchPayload: parsed, contentWithoutBlock };
  } catch {
    return { matchPayload: null, contentWithoutBlock };
  }
}
